<template>
    <div>
        <div
            @click="openDrawer"
            data-testid="add-dartbot-button"
            class="relative isolate flex min-h-[72px] w-full cursor-pointer items-center justify-between gap-4 border border-[#2B2B2B] bg-[#232323] p-4 transition-colors hover:border-red-500 md:max-w-122"
        >
            <div class="flex min-w-[130px] flex-col gap-2">
                <h4
                    class="font-title h-4 w-[108px] text-xs leading-4 font-extrabold tracking-[0.25px] text-white uppercase [text-shadow:-2px_2px_4px_rgba(0,0,0,0.25)]"
                >
                    {{ $t('bot_selection.title') }}
                </h4>
                <h5 class="h-4 text-xs leading-4 font-normal tracking-[0.25px] text-white">
                    {{ $t('bot_selection.difficulty_levels', { n: dartbots.length }) }}
                </h5>
            </div>
            <img
                src="@/assets/images/dartbot.png"
                class="absolute top-[-9px] left-[62%] z-1 h-20 w-20 -translate-x-1/2 object-contain object-top filter-[drop-shadow(-2px_-2px_3px_rgba(0,0,0,0.5))] md:left-[168px] md:translate-x-0"
            />
            <div
                class="z-2 flex h-10 w-10 items-center justify-center rounded p-2 backdrop-blur-sm"
                style="
                    background: linear-gradient(
                        134.75deg,
                        rgba(255, 255, 255, 0.0375) 0.22%,
                        rgba(255, 255, 255, 0.0125) 99.78%
                    );
                "
            >
                <svg
                    class="size-4"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M7 -3.0598e-07L7 5L12 5L12 7L7 7L7 12L5 12L5 7L3.0598e-07 7L2.18557e-07 5L5 5L5 -2.18557e-07L7 -3.0598e-07Z"
                        fill="#FF2C2C"
                    />
                </svg>
            </div>
        </div>

        <SimpleDrawer hashKey="bot-difficulty-selection">
            <div class="flex flex-col items-center gap-8 p-6">
                <h3 class="w-full text-lg font-light text-white">{{ $t('bot_selection.choose_difficulty') }}</h3>
                <Avatar :src="selectedBot.avatar" :size="20" />
                <CustomSlider v-model="sliderValue" :min="0" :max="maxSliderValue" :step="1" />
                <div class="w-full">
                    <div class="text-center font-semibold text-white">
                        {{ $t('bot_selection.level', { current: sliderValue + 1, total: dartbots.length }) }}
                    </div>
                    <div class="text-center font-light text-white">
                        {{ selectedBot.average }} {{ $t('bot_selection.avg_label') }}
                    </div>
                </div>

                <!-- Add Button -->
                <DiagonalCutButton
                    @click="addSelectedBot"
                    :disabled="isAdding"
                    variant="primary"
                    cut-position="left-top"
                    class="w-full"
                    data-testid="confirm-dartbot-button"
                >
                    <span v-if="isAdding" class="text-lg">{{ $t('bot_selection.adding') }}</span>
                    <span v-else class="text-lg">{{ $t('general.confirm') }}</span>
                </DiagonalCutButton>
            </div>
        </SimpleDrawer>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useMatchStore } from '@/stores/match.js'
import SimpleDrawer from '@/components/ui/SimpleDrawer.vue'
import Avatar from '@/components/Avatar.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import CustomSlider from '@/components/ui/CustomSlider.vue'

export default {
    name: 'BotSelection',
    components: {
        SimpleDrawer,
        Avatar,
        DiagonalCutButton,
        CustomSlider,
    },
    data() {
        return {
            sliderValue: 0,
            isAdding: false,
        }
    },
    computed: {
        ...mapStores(useAuthStore, useMatchStore),

        sortedDartbots() {
            // Sort bots by average in ascending order (easiest to hardest)
            return [...(this.authStore.dartbots || [])].sort((a, b) => a.average - b.average)
        },

        dartbots() {
            return this.sortedDartbots
        },

        maxSliderValue() {
            return Math.max(0, this.dartbots.length - 1)
        },

        selectedBot() {
            if (this.dartbots.length === 0) return null
            return this.dartbots[this.sliderValue] || this.dartbots[0]
        },
    },
    methods: {
        openDrawer() {
            this.$router.push({ hash: '#bot-difficulty-selection' })
        },

        async addSelectedBot() {
            if (!this.selectedBot) return

            this.isAdding = true
            try {
                await this.matchStore.addDartbotToMatch(this.selectedBot)
                this.$router.replace({ hash: '' })
                this.$toast(
                    this.$t('bot_selection.added_title'),
                    3000,
                    this.$t('bot_selection.added_body', { name: this.selectedBot.name }),
                )
            } catch (error) {
                console.error('Failed to add dartbot:', error)
                this.$toast(
                    this.$t('bot_selection.error_title'),
                    5000,
                    error.message || this.$t('bot_selection.error_body'),
                )
            } finally {
                this.isAdding = false
            }
        },
    },
    watch: {
        dartbots: {
            immediate: true,
            handler(newBots) {
                // Reset slider to middle position when bots are loaded
                if (newBots && newBots.length > 0) {
                    this.sliderValue = Math.floor(newBots.length / 2)
                }
            },
        },
    },
}
</script>
